import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import {
    DeleteTradingAlgoRequest,
    GetTradingAlgosRequest,
    GetTradingAlgosResponse,
    PutTradingAlgosRequest,
    TradingAlgo,
    TradingAlgoStored,
    TradingSubPageProps
} from "../utils/typesTrading";
import {AuthContext} from "../components/AuthContext";
import {deleteTradingAlgo, getTradingAlgos, putTradingAlgo} from "../apis/junkyard";
import {
    Box, Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Drawer,
    IconButton,
    Select,
    Tooltip,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {Bot, Folder} from 'lucide-react';
import {AIAssistantPanel} from "../components/AIAssistantPanel";
import {CollapsibleSidebar} from "../components/CollapsibleSidebar";
import DeleteIcon from '@mui/icons-material/Delete';
import {ContentCopy, Download, Save} from "@mui/icons-material";
import {python} from '@codemirror/lang-python';
import {vscodeDark} from '@uiw/codemirror-theme-vscode';
import CodeMirror from '@uiw/react-codemirror';
import {pythonCompletions} from "../utils/editor_utils";
import {keymap} from '@codemirror/view';
import {vim} from '@replit/codemirror-vim';
import {indentUnit} from '@codemirror/language'
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {Prec} from '@codemirror/state';
import {TradingAlgoList} from "../components/TradingAlgoList";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {useNavigate} from "react-router";
import {acceptCompletion, autocompletion} from "@codemirror/autocomplete";
import Typography from "@mui/material/Typography";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {formatDateForAPI} from "../utils/datetime";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import {subscriptionTierToBacktestYearsLookbackLimit} from "../utils/types";
import {useSnackbar} from "../components/SnackbarContext";
import {unifiedMergeView, acceptChunk, getChunks} from '@codemirror/merge';
import {ReactCodeMirrorRef} from '@uiw/react-codemirror';
// import utc from 'dayjs/plugin/utc';
// import timezone from 'dayjs/plugin/timezone';
//
// dayjs.extend(utc);
// dayjs.extend(timezone);



export const TradingSubPage: React.FC<TradingSubPageProps> = (props) => {
    const authContext = useContext(AuthContext);
    const {showSnackbar} = useSnackbar();
    const navigate = useNavigate()
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false);
    const [mobileDrawerTab, setMobileDrawerTab] = useState<string>('strategies');
    const [tradingAlgos, setTradingAlgos] = React.useState<TradingAlgoStored[] | undefined>( undefined);
    const [numPages, setNumPages] = React.useState<number | null>(1);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const defaultYearsLookback = 2;
    const DEFAULT_BACKTEST_START_DATE = new Date(new Date().setFullYear(new Date().getFullYear() - defaultYearsLookback));
    const DEFAULT_BACKTEST_END_DATE = new Date(Date.now());
    const [backtestStartDate, setBacktestStartDate] = React.useState<Date>(DEFAULT_BACKTEST_START_DATE);
    const [backtestEndDate, setBacktestEndDate] = React.useState<Date>(DEFAULT_BACKTEST_END_DATE);
    const [backtestRangeInvalid, setBacktestRangeInvalid] = React.useState<boolean>(false);
    const [selectedTradingAlgo, setSelectedTradingAlgo] = React.useState<TradingAlgo | null>(null);
    const [strategyName, setStrategyName] = useState<string>(selectedTradingAlgo?.strategy_name || '');
    const [isDirty, setIsDirty] = useState(false);
    const [code, setCode] = useState<string>(selectedTradingAlgo?.code || '');
    const [isVimMode, setIsVimMode] = useState<boolean>(false);
    const [backtestStartingCash, setBacktestStartingCash] = useState<number>(10000.0);
    const [backtestStartingCashInvalid, setBacktestStartingCashInvalid] = useState<boolean>(false);
    const [tickersUniverse, setTickersUniverse] = useState<string>("usEquities")
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState<boolean>(false);
    const [aiProposal, setAiProposal] = useState<{original: string; proposed: string} | null>(null);
    const [proposalSeq, setProposalSeq] = useState<number>(0);
    const editorRef = useRef<ReactCodeMirrorRef>(null);

    useEffect(() => {
        if (selectedTradingAlgo) {
            setStrategyName(selectedTradingAlgo.strategy_name || '');
            setIsDirty(false);
        }
        // Drop any pending AI proposal so a newly selected algorithm's editor
        // never shows the previous algorithm's diff.
        setAiProposal(null);
    }, [selectedTradingAlgo])


    useEffect(() => {
        getAlgos();
    }, [authContext, currentPage]);


    async function getAlgos() {

        // Extract query parameters from URL
        const access_token = authContext.readAccessToken();
        console.debug("Auth context access token", access_token);

        if (access_token !== null) {
            try {
                const resp = await getTradingAlgos(
                    {
                        "access_token": access_token,
                        "page": currentPage,
                        "per_page": 90
                    } as GetTradingAlgosRequest
                )
                const response = resp as GetTradingAlgosResponse;
                console.debug("Response", response)

                setTradingAlgos(response.trading_algos || []);
                if (response.total_pages) {
                    setNumPages(response.total_pages);
                }
                console.debug("Got trading algorithms", tradingAlgos)
            } catch (error) {
                console.error(error);
            }

        }
    }

    const handleProposeEdits = (proposed: string, original: string) => {
        setAiProposal({original, proposed});
        setProposalSeq((s) => s + 1);
    };

    const handleAcceptAllAiEdits = () => {
        const view = editorRef.current?.view;
        if (view) {
            // Accept outstanding chunks one at a time — each acceptance
            // changes the doc and invalidates the previously computed chunk
            // positions, so re-query after every step. Chunks already accepted
            // (or rejected) during the session are no longer outstanding.
            let result = getChunks(view.state);
            while (result && result.chunks.length) {
                if (!acceptChunk(view, result.chunks[0].fromB)) break;
                result = getChunks(view.state);
            }
            handleCodeChange(view.state.doc.toString());
        }
        setAiProposal(null);
    };

    const handleRejectAllAiEdits = () => {
        // Clearing the proposal remounts the editor with the pre-proposal
        // code, discarding the diff (including hunks accepted during the
        // session) — "reject all" means discard the proposal entirely.
        setAiProposal(null);
    };

    const getNewAlgorithmCode = (): string => {
        return `

class MyTradingAlgorithm(TradingAlgorithm):

    def initialize(self):
        """
        Anything that needs to be done at the beginning
        """
        pass

    def trading_logic(self, current_date: date) -> List[PositionChange]:
        """
        Executes the trading logic for the specified date by determining suitable buy
        actions for available tickers. It divides the available cash in the portfolio
        equally among tickers and attempts to create a \`PositionChange\` for each
        untouched ticker.

        Parameters
        ----------
        current_date : date
            The date for which the trading logic is executed.

        Returns
        -------
        List[PositionChange]
            A list of \`PositionChange\` objects representing the buy actions for the
            specified date. Each entry contains details about the trade including
            the ticker, price, number of shares, trade action, and trade date.

        """
        tickers = ["NVDA", "AAPL", "CVS", "JPM", "DIS", "ACI", "TGT"]
        num_tickers = len(tickers)
        cash_per_ticker = self.portfolio.cash / num_tickers
        position_changes = []

        for ticker in tickers:
            if not self.portfolio.get_position(ticker):
                try:
                    current_price = self.broker.get_price_for_ticker_on_date(ticker=ticker, purchase_date=current_date)
                    position_change = PositionChange(trade_date=current_date, ticker=ticker, price=current_price, action="BUY", shares=cash_per_ticker / current_price)
                    position_changes.append(position_change)
                    logger.info(f"Success adding position change for ticker {ticker} on date {current_date.isoformat()}")
                except ValueError as e:
                    logger.error(f"Failed to add position change for ticker {ticker} on date {current_date.isoformat()}")

        return position_changes
        
"""
class Position:
    def __init__(self, ticker: str, quantity: float, price: float):
        self.ticker = ticker
        self.quantity = quantity
        self.price = price
        
        
class PositionChange:

    def __init__(self, trade_date: date, ticker: str, price: float, action: str, shares: float):
        self.trade_date = trade_date
        self.ticker = ticker
        self.price = price
        self.action = action
        self.shares = shares
        

class Portfolio:
    def __init__(self, cash: float, positions: list[Position]):
        self.cash = cash
        self.positions = positions

    @property
    def portfolio_value(self) -> float:
        ...

    @property
    def positions_value_without_cash(self) -> float:
        ...

    def buy(self, ticker: str, quantity: float, price: float, trade_date: date) -> bool:
        ...

    def sell(self, ticker, quantity: float, price: float, trade_date: date) -> bool:
        ...

    def get_position(self, ticker: str) -> Position | None:
        ...
"""
        `

    }

    const onCreateNewAlgorithm = () => {

        const currentDateTime = new Date(Date.now());

        let newTradingAlgo = {
            "id": undefined,
            "strategy_name": "New Strategy",
            "code": getNewAlgorithmCode(),
            "last_modified_datetime_utc": currentDateTime.toISOString(),
            "language": 'python',
            "tag": 'algorithms',
            "folder": 'default',
            "source": 'junkyard',
        } as TradingAlgo;

        setSelectedTradingAlgo(null);
        setIsDirty(true);
        setStrategyName('New Strategy');
        onSelectedTradingAlgoChanged(newTradingAlgo);
    }

    const onSelectedTradingAlgoChanged = (newTradingAlgo: TradingAlgo) => {
        setSelectedTradingAlgo(newTradingAlgo);
        setCode(newTradingAlgo.code);
        if (isMobile) setMobileDrawerOpen(false);
    }

    const handleBacktest = () => {
        // navigate to the results view page
        if (!selectedTradingAlgo) return;
        if (!selectedTradingAlgo.id) return;

        navigate('/backtest', {
            state: {
                trading_algo: selectedTradingAlgo,
                backtestStartDate: backtestStartDate,
                backtestEndDate: backtestEndDate,
                startingCash: backtestStartingCash,
                tickersUniverse: tickersUniverse,
            }
        })
    };

    const handleCopyCode = () => {
        try {
            navigator.clipboard.writeText(code);
        } catch (error) {
            console.error("An error occurred while attempting to copy code: ", error);
        }

    };

    const handleCodeChange = (newValue: string | undefined) => {
        if (newValue && newValue !== code) {
            setIsDirty(true);
            setCode(newValue);
        }
    }

    const onStrategyNameUpdate = (newValue: string) => {
        if (!selectedTradingAlgo) return;
        setIsDirty(true);
        selectedTradingAlgo.strategy_name = newValue
        setStrategyName(newValue)
        setSelectedTradingAlgo(selectedTradingAlgo)
    }

    const handleSaveCode = async () => {

        if (!selectedTradingAlgo) return;

        selectedTradingAlgo.code = code
        setSelectedTradingAlgo(selectedTradingAlgo);

        try {
            const request = {
                'access_token': authContext.readAccessToken(),
                'trading_algo': selectedTradingAlgo

            } as PutTradingAlgosRequest;
            const putTradingAlgoResponse = await putTradingAlgo(request);
            if (putTradingAlgoResponse.trading_algo && putTradingAlgoResponse.status === "Success") {
                setSelectedTradingAlgo(putTradingAlgoResponse.trading_algo);
                setIsDirty(false);
                showSnackbar("Algorithm saved successfully", "info");
            } else {
                showSnackbar("An error occurred while saving the new algorithm. You may be exceeding current tier limits.", "error");
            }
            await getAlgos();
        } catch (error) {
            console.error(error);
        }

    }

    const handleDelete = async () => {
        if (!selectedTradingAlgo) return;

        try {
            const request = {
                'access_token': authContext.readAccessToken(),
                'trading_algo': selectedTradingAlgo

            } as DeleteTradingAlgoRequest;
            const deleteTradingAlgoResponse = await deleteTradingAlgo(request);
            if (deleteTradingAlgoResponse.status === "Success") {
                selectedTradingAlgo.code = ''
                setSelectedTradingAlgo(null);
            }
            await getAlgos();
        } catch (error) {
            console.error(error);
        }

    }

    const handleVimModeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsVimMode(event.target.checked)
    }

    const handleBacktestStartingCashChanged = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const v = parseFloat(event.target.value);
        if (!isNaN(v) && v > 0 && v < 1000000000000) {
            setBacktestStartingCashInvalid(false);
            setBacktestStartingCash(v);
        } else {
            setBacktestStartingCashInvalid(true);
        }
    }

    const handleDownloadCode = () => {
        const blob = new Blob([code], { type: 'text/python' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (selectedTradingAlgo?.strategy_name || 'download') + '.py';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };


    useEffect(() => {
        async function checkBacktestDateRange() {
            if (!authContext.isAuthenticated()) {
                return
            }

            // Extract query parameters from URL
            try {
                const tier = authContext.getSubscriptionTier();
                const yearsLimit = subscriptionTierToBacktestYearsLookbackLimit(tier);
                const yearsLimitMilliseconds = yearsLimit * 365 * 24 * 60 * 60 * 1000;
                if ((backtestEndDate.getTime() - backtestStartDate.getTime()) > yearsLimitMilliseconds) {
                    showSnackbar("Backtest date range exceeds limit for current subscription tier.", "error")
                    setBacktestRangeInvalid(true);
                } else {
                    setBacktestRangeInvalid(false);
                }
            } catch (error) {
                console.error(error);
            }
        }
        checkBacktestDateRange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [backtestStartDate, backtestEndDate]);

    const sidebarTabs = [
        {
            id: 'strategies',
            label: 'Strategies',
            icon: <Folder size={16}/>,
            content: (
                <TradingAlgoList
                    algos={tradingAlgos || []}
                    selectedAlgoChanged={onSelectedTradingAlgoChanged}
                    numPages={numPages}
                    onPageChanged={(page: number | null) =>  page !== null && setCurrentPage(page)}
                    onCreateNewAlgo={onCreateNewAlgorithm}
                />
            ),
        },
        {
            id: 'ai-assistant',
            label: 'AI Assistant',
            icon: <Bot size={16}/>,
            content: <AIAssistantPanel tradingAlgo={selectedTradingAlgo} currentCode={code} onProposeEdits={handleProposeEdits}/>,
        },
    ];

    const openMobileDrawer = (tabId: string) => {
        setMobileDrawerTab(tabId);
        setMobileDrawerOpen(true);
    };

    return (
        <Box sx={{
            ml: {xs: 1.5, md: 5},
            mr: {xs: 1.5, md: 5},
            mt: 12,
            mb: 5
        }}>
            {isMobile && (
                <Box sx={{display: 'flex', gap: 1, mb: 1.5}}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Folder size={16}/>}
                        onClick={() => openMobileDrawer('strategies')}
                    >
                        Strategies
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Bot size={16}/>}
                        onClick={() => openMobileDrawer('ai-assistant')}
                    >
                        AI Assistant
                    </Button>
                </Box>
            )}
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start'
            }}>
                {!isMobile && (
                    <CollapsibleSidebar tabs={sidebarTabs}/>
                )}

                {selectedTradingAlgo !== null ? (
                    <Box sx={{
                        flex: 1,
                        ml: {md: 5, xs: 0},
                        maxHeight: {md: '90vh'},
                        minWidth: 300,
                        transition: 'margin-left 0.2s ease'
                    }}>
                        <Box
                            sx={{
                                p: 2,
                                pl: 0,
                                borderBottom: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                columnGap: 2,
                                rowGap: 1.5
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', rowGap: 1.5, flex: '2 1 480px' }}>
                                <Box sx={{ flex: '1 1 220px', minWidth: 150}}>
                                    <TextField
                                        label="Strategy Name"
                                        id="outlined-size-small"
                                        size="small"
                                        fullWidth
                                        value={strategyName}
                                        onChange={
                                            (changeEvent) => {
                                                setIsDirty(true);
                                                onStrategyNameUpdate(changeEvent.target.value)
                                            }
                                        }
                                    />
                                </Box>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Start Date"
                                        minDate={dayjs(new Date('2016-01-01'))}
                                        maxDate={dayjs(new Date(Date.now()))}
                                        value={dayjs(formatDateForAPI(backtestStartDate))}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                setBacktestStartDate(newValue.toDate())}
                                            }
                                        }
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { flex: '1 1 150px', minWidth: 130 }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="End Date"
                                        minDate={dayjs(new Date('2016-01-01'))}
                                        maxDate={dayjs(new Date(Date.now()))}
                                        value={dayjs(backtestEndDate)}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                setBacktestEndDate(newValue.toDate())}
                                            }
                                        }
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { flex: '1 1 150px', minWidth: 130 }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                <TextField
                                    type="number"
                                    label="Starting Cash ($)"
                                    defaultValue={`${backtestStartingCash}`}
                                    onChange={handleBacktestStartingCashChanged}
                                    error={backtestStartingCashInvalid}
                                    sx={{ flex: '1 1 150px', minWidth: 130 }}
                                />

                                <FormControl sx={{ flex: '1 1 170px', minWidth: 160, maxWidth: 200 }}>
                                    <InputLabel id="tickers-universe-label">Tickers Universe</InputLabel>
                                    <Select
                                        labelId="tickers-universe-label"
                                        id="tickers-universe"
                                        value={tickersUniverse}
                                        label="Tickers Universe"
                                        onChange={(event) => setTickersUniverse(event.target.value)}
                                    >
                                        <MenuItem value={"usEquities"}>US Equities</MenuItem>
                                        <MenuItem value={"fortune500"}>Fortune 500 (2025)</MenuItem>
                                    </Select>
                                </FormControl>
                                <Box sx={{ display: 'flex', justifyContent: 'right', flexWrap: 'wrap', alignItems: 'center', gap: 1, rowGap: 1.5, flex: '1 1 auto' }}>
                                    <Tooltip title="Backtest">
                                        <Button
                                            variant="contained"
                                            color="success"
                                            onClick={handleBacktest}
                                            sx={{flex: '0 0 auto'}}
                                            disabled={backtestStartingCashInvalid || backtestRangeInvalid}
                                        >
                                            Backtest
                                        </Button>
                                    </Tooltip>
                                    <Box sx={{ml: {md: 5, xs: 0}}}>
                                        <Tooltip title="VimMode">
                                            <FormControlLabel control={<Checkbox onChange={handleVimModeChange}/>} label="Vim Mode" />
                                        </Tooltip>
                                    </Box>
                                    <Tooltip title="Copy Code">
                                        <IconButton onClick={handleCopyCode} size="small">
                                            <ContentCopy />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download Code">
                                        <IconButton onClick={handleDownloadCode} size="small">
                                            <Download />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Save">
                                        <IconButton
                                            onClick={handleSaveCode}
                                            size="small"
                                            disabled={!isDirty}
                                        >
                                            <Save />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton aria-label="delete" size="small" onClick={() => setIsDeleteDialogOpen(true)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                        </Box>
                        {aiProposal && (
                            <Box sx={{display: 'flex', gap: 1}}>
                                <Chip label="Accept all AI edits" color="primary" onClick={handleAcceptAllAiEdits}/>
                                <Chip label="Reject all" onClick={handleRejectAllAiEdits}/>
                            </Box>
                        )}
                        <CodeMirror
                            ref={editorRef}
                            key={aiProposal ? `ai-${proposalSeq}` : selectedTradingAlgo?.id ?? 'new'}
                            theme={vscodeDark}
                            value={aiProposal ? aiProposal.proposed : code}
                            height={isMobile ? '70vh' : '90vh'}
                            style={{fontSize: 14, minWidth: isMobile ? 0 : 1180}}
                            extensions={[
                                isVimMode ? vim() : [],
                                python(),
                                indentUnit.of('    '),
                                autocompletion({activateOnTyping: true, override: [pythonCompletions]}),
                                Prec.highest(keymap.of([{key: 'Tab', run: acceptCompletion}])),
                                ...(aiProposal
                                    ? [unifiedMergeView({
                                        original: aiProposal.original,
                                        highlightChanges: true,
                                        gutter: true,
                                        collapseUnchanged: {margin: 3, minSize: 4},
                                    })]
                                    : []),
                            ]}
                            onChange={(value) => {
                                // While an AI proposal is open, `code` stays frozen at the
                                // pre-proposal state; it is committed only on "accept all",
                                // and the remount on reject restores it.
                                if (!aiProposal) handleCodeChange(value);
                            }}
                            basicSetup={{lineNumbers: true, foldGutter: true, highlightActiveLine: true}}
                        />
                    </Box>
                ) : (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: "100%",
                        m: 2
                    }}>
                        <Typography variant="h4">
                            Create or select a strategy
                        </Typography>
                    </Box>
                )}
            </Box>

            <Drawer
                anchor="left"
                open={isMobile && mobileDrawerOpen}
                onClose={() => setMobileDrawerOpen(false)}
            >
                {isMobile && (
                    <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                        <CollapsibleSidebar
                            key={mobileDrawerTab}
                            initialTabId={mobileDrawerTab}
                            tabs={sidebarTabs}
                            onCollapse={() => setMobileDrawerOpen(false)}
                        />
                    </Box>
                )}
            </Drawer>

            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                fullWidth
            >
                <DialogTitle>Delete strategy?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This action is permanent and cannot be undone. Are you sure you want to delete "{selectedTradingAlgo?.strategy_name}"?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        onClick={() => {
                            setIsDeleteDialogOpen(false);
                            handleDelete();
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
